import { useId, type KeyboardEvent } from "react";
import { InputGroup } from "@/components/ui/input-group";
import { DatePickerPopover } from "./DatePickerPopover";
import { MaskedDateInput } from "./MaskedDateInput";
import type { FormDatePickerProps } from "./types";
import { useFormDatePicker } from "./useFormDatePicker";

export function FormDatePicker({
  disabled,
  ariaDescribedBy,
  ...options
}: FormDatePickerProps) {
  const errorId = useId();
  const {
    mask,
    text,
    month,
    open,
    setOpen,
    errorMessage,
    showInlineError,
    selectedDate,
    isDateDisabled,
    allowOlderDates,
    pickDateLabel,
    todayLabel,
    placeholder,
    setMonth,
    applyCommittedDate,
    handleInputAccept,
    handleInputBlur,
    handleToday,
    onFocus,
  } = useFormDatePicker(options);

  const describedBy =
    ariaDescribedBy ??
    (showInlineError && errorMessage ? errorId : undefined);

  return (
    <div className="w-full">
      <InputGroup data-disabled={disabled || undefined}>
        <MaskedDateInput
          mask={mask}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          invalid={!!errorMessage}
          ariaDescribedBy={describedBy}
          onAccept={handleInputAccept}
          onFocus={onFocus}
          onBlur={handleInputBlur}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <DatePickerPopover
          open={open}
          onOpenChange={setOpen}
          pickDateLabel={pickDateLabel}
          todayLabel={todayLabel}
          disabled={disabled}
          selected={selectedDate}
          month={month}
          onMonthChange={setMonth}
          onSelect={applyCommittedDate}
          isDateDisabled={isDateDisabled}
          allowOlderDates={allowOlderDates}
          onToday={handleToday}
        />
      </InputGroup>
      {showInlineError && errorMessage && (
        <p id={errorId} className="text-destructive mt-1.5 text-sm">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
